/**
 * @fileoverview MOT Expiry Widget - Shows vehicles with expiring MOT.
 * Displays summary counts and allows sending reminders.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Tooltip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Badge,
  Collapse,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  DirectionsCar as CarIcon,
  Phone as PhoneIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import api from '../services/api';

/**
 * MOTExpiryWidget - Dashboard widget showing vehicles with expiring MOT.
 *
 * @param {Object} props
 * @param {number} props.limit - Max vehicles to show per category (default: 5)
 * @returns {JSX.Element}
 */
const MOTExpiryWidget = ({ limit = 5, defaultExpanded = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ summary: {}, vehicles: {} });
  const [activeTab, setActiveTab] = useState(0);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [expanded, setExpanded] = useState(defaultExpanded);

  const tabs = [
    { key: 'expired', label: 'Expired', color: 'error' },
    { key: 'this_week', label: 'This Week', color: 'warning' },
    { key: 'next_week', label: 'Next Week', color: 'info' },
    { key: 'this_month', label: 'This Month', color: 'success' },
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/v1/dashboard/mot-expiry/?limit=${limit}`);
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load MOT expiry data');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSendReminder = async (vehicleId, registration) => {
    if (!window.confirm(`Send MOT reminder SMS for ${registration}?`)) {
      return;
    }

    try {
      setSendingReminder(vehicleId);
      await api.post('/v1/reminders/send-mot/', { vehicle_id: vehicleId });
      setSuccessMessage(`Reminder sent for ${registration}`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getDaysUntilExpiry = (dateStr) => {
    if (!dateStr) return null;
    const expiry = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  };

  if (loading && !data.summary?.total) {
    return (
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <CarIcon />
              <Typography variant="h6">MOT Expiry Tracker</Typography>
              <CircularProgress size={20} />
            </Box>
          }
        />
      </Card>
    );
  }

  const currentTab = tabs[activeTab];
  const vehicles = data.vehicles?.[currentTab.key] || [];

  return (
    <Card>
      <CardHeader
        onClick={() => setExpanded(!expanded)}
        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <CarIcon />
            <Typography variant="h6">MOT Expiry Tracker</Typography>
            {data.summary?.total > 0 && (
              <Chip
                label={data.summary.total}
                color="warning"
                size="small"
              />
            )}
            {!expanded && data.summary?.expired > 0 && (
              <Chip
                label={`${data.summary.expired} expired`}
                color="error"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        }
        action={
          <Box display="flex" alignItems="center">
            <Tooltip title="Refresh">
              <IconButton onClick={(e) => { e.stopPropagation(); fetchData(); }} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        }
      />
      <Collapse in={expanded}>
        <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {/* Summary chips */}
        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          {tabs.map((tab) => (
            <Chip
              key={tab.key}
              icon={tab.key === 'expired' ? <WarningIcon /> : <ScheduleIcon />}
              label={`${tab.label}: ${data.summary?.[tab.key] || 0}`}
              color={tab.color}
              variant={data.summary?.[tab.key] > 0 ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Box>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.key}
              label={
                <Badge
                  badgeContent={data.summary?.[tab.key] || 0}
                  color={tab.color}
                  max={99}
                >
                  {tab.label}
                </Badge>
              }
            />
          ))}
        </Tabs>

        {/* Vehicles table */}
        {vehicles.length === 0 ? (
          <Box textAlign="center" py={3}>
            <Typography color="text.secondary">
              No vehicles in this category
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>MOT Expiry</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vehicles.map((vehicle) => {
                  const daysLeft = getDaysUntilExpiry(vehicle.mot_expiry_date);
                  return (
                    <TableRow key={vehicle.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {vehicle.registration}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {vehicle.make} {vehicle.model}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {vehicle.customer_name}
                        </Typography>
                        {vehicle.customer_phone && (
                          <Typography variant="caption" color="text.secondary">
                            {vehicle.customer_phone}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatDate(vehicle.mot_expiry_date)}
                          size="small"
                          color={
                            daysLeft < 0 ? 'error' :
                            daysLeft <= 7 ? 'warning' :
                            daysLeft <= 14 ? 'info' : 'success'
                          }
                        />
                        {daysLeft !== null && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {daysLeft < 0
                              ? `${Math.abs(daysLeft)} days ago`
                              : daysLeft === 0
                              ? 'Today'
                              : `${daysLeft} days left`}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {vehicle.customer_phone && (
                          <Tooltip title="Send SMS Reminder">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSendReminder(vehicle.id, vehicle.registration)}
                                disabled={sendingReminder === vehicle.id}
                              >
                                {sendingReminder === vehicle.id ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <SendIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {vehicle.customer_phone && (
                          <Tooltip title="Call Customer">
                            <IconButton
                              size="small"
                              onClick={() => window.location.href = `tel:${vehicle.customer_phone}`}
                            >
                              <PhoneIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {data.summary?.[currentTab.key] > limit && (
          <Box textAlign="center" mt={2}>
            <Typography variant="caption" color="text.secondary">
              Showing {limit} of {data.summary[currentTab.key]} vehicles
            </Typography>
          </Box>
        )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default MOTExpiryWidget;
